// Handles EmptyState and ErrorState
import { useMemo, useState } from 'react';
import { templateHooks } from '../network/endpoints.js';
import { type TemplateListItem } from '../network/types.js';
import { DataTable } from '@repo/web-list';
import { defineListPage } from '@repo/list';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Button } from '../../../components/ui/Button.js';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { TemplateEditor } from './TemplateEditor.js';
import { format } from 'date-fns';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';

const CATEGORY_MAP: Record<string, string> = {
  auth: 'Auth',
  scheme: 'Scheme',
  enrollment: 'Enrollment',
  auction: 'Auction',
  dues: 'Dues',
  prize: 'Prize',
  surety: 'Surety',
  maker_checker: 'Maker Checker',
  kyc: 'KYC',
  exit: 'Exit',
  noc: 'NOC',
};

export function TemplateList() {
  const [page, setPage] = useState(1);
  const limit = 20;
  const { toast } = useToast();

  const { data, isLoading, refetch } = templateHooks.useList({ page, limit });

  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const { data: selectedTemplate, isLoading: isLoadingDetail } = templateHooks.useGet(selectedTemplateId || '');

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const deleteMutation = templateHooks.useRemove();

  const config = defineListPage<TemplateListItem>({
    variant: 'data-table',
    resource: 'items',
    columns: [
      { id: 'templateKey', header: 'Template Key', accessor: 'templateKey', renderType: 'code' },
      { id: 'channel', header: 'Channel', accessor: 'channel', renderType: 'text' },
      { id: 'locale', header: 'Locale', accessor: 'locale', renderType: 'text' },
      { id: 'updatedAt', header: 'Last Updated', accessor: 'updatedAt', renderType: 'datetime' },
      { id: 'actions', header: '', accessor: () => null, width: 140 },
    ],
  });

  const groupedTemplates = useMemo(() => {
    if (!data?.data) return {};
    const groups: Record<string, TemplateListItem[]> = {};
    
    data.data.forEach((template: TemplateListItem) => {
      const prefix = template.templateKey.split('.')[0];
      const category = CATEGORY_MAP[prefix] || 'Other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(template);
    });

    return groups;
  }, [data]);

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteMutation.mutateAsync(confirmDeleteId);
      setConfirmDeleteId(null);
      toast({ type: 'success', title: 'Deleted', description: 'Template deleted successfully.' });
      refetch();
    // allow-silent-catch: error is surfaced to the user via toast(), not console/logger
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete template.';
      toast({ type: 'error', title: 'Error', description: message });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notification Templates"
        subtitle="Manage templates across all channels and locales"
        action={
          <Button
            onClick={() => {
              setSelectedTemplateId(null);
              setEditorOpen(true);
            }}
          >
            Create Template
          </Button>
        }
      />

      {isLoading ? (
        <DataTable onRetry={() => window.location.reload()}
          config={config}
          data={[]}
          total={0}
          page={page}
          limit={limit}
          onPageChange={setPage}
          loading
        />
      ) : Object.keys(groupedTemplates).length === 0 ? (
        <DataTable onRetry={() => window.location.reload()}
          config={config}
          data={[]}
          total={0}
          page={page}
          limit={limit}
          onPageChange={setPage}
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedTemplates).map(([category, templates]) => (
            <div key={category} className="space-y-3">
              <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider px-1">
                {category}
              </h3>
              <DataTable onRetry={() => window.location.reload()}
                config={config}
                data={templates}
                total={templates.length}
                page={1}
                limit={templates.length}
                onPageChange={() => {}}
                className="border-0 shadow-none"
                customRenderers={{
                  channel: (row: TemplateListItem) => (
                    <StatusBadge
                      status={row.channel === 'SMS' ? 'info' : row.channel === 'EMAIL' ? 'success' : 'info'}
                      label={row.channel}
                    />
                  ),
                  locale: (row: TemplateListItem) => (
                    <StatusBadge status="info" label={row.locale.toUpperCase()} />
                  ),
                  updatedAt: (row: TemplateListItem) => format(new Date(row.updatedAt), 'MMM d, yyyy HH:mm'),
                  actions: (row: TemplateListItem) => (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTemplateId(row.id);
                          setEditorOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-danger-500 hover:text-danger-700 hover:bg-danger-50"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteId(row.id);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  ),
                }}
              />
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setSelectedTemplateId(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplateId ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>

          {selectedTemplateId && isLoadingDetail ? (
            <div className="p-4 space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-10 w-32" />
            </div>
          ) : (
            <TemplateEditor
              initialData={selectedTemplate}
              onSave={() => {
                setEditorOpen(false);
                setSelectedTemplateId(null);
                refetch();
              }}
              onCancel={() => {
                setEditorOpen(false);
                setSelectedTemplateId(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDeleteId}
        title="Delete Template?"
        description="Are you sure you want to delete this template? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteId(null)}
        loading={deleteMutation.isPending}
        confirmLabel="Delete"
      />
    </div>
  );
}
